import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { Provider } from '@as-react/common-types';
import { ClockIcon, MapPinIcon } from '@yonderland/andes-icons';
import { Box, Icon, SelectionCard, StackBlock, StackInline, TextAlpha } from '@yonderland/andes-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useDeliveryState } from '../../../../context/CheckoutDeliveryContext';
import { formatTimeStampToDateParts, getAllWeekdays } from '../../../../util/DateUtil';
import { round } from '../../../../util/NumberUtil';
import PickupPointUtil from '../../../../util/PickupPointUtil';
import { OpeningHours } from '../../../opening-hours/OpeningHours';
const LOCALE_EN = 'en-GB';
const DATE_FORMAT = 'YYYY-MM-DD';
const HOUR_PLACEHOLDER = '00:00';
/**
 * Sorts the opening hours array by the current day of the week
 */
export const sortOpeningHours = (openingHours) => {
    const today = dayjs();
    const { weekday: weekdayToday } = formatTimeStampToDateParts(today.toDate().getTime(), LOCALE_EN, {
        weekday: 'long',
    });
    // get all weekdays in English because the API returns them in English
    const weekdaysEN = getAllWeekdays(today.toDate(), LOCALE_EN);
    const allWeekdays = weekdaysEN.map((weekday, index) => {
        const knownOpeningHours = openingHours.find(schedule => schedule.dayOfWeek === weekday);
        const day = today.add(index, 'day');
        const date = day.format(DATE_FORMAT);
        return knownOpeningHours
            ? {
                date,
                weekday,
                closed: false,
                openingHour: knownOpeningHours.openingTime,
                closingHour: knownOpeningHours.closingTime,
            }
            : {
                date,
                weekday,
                closed: true,
                openingHour: HOUR_PLACEHOLDER,
                closingHour: HOUR_PLACEHOLDER,
            };
    });
    const indexCurrentDate = allWeekdays.findIndex(schedule => schedule.weekday === weekdayToday);
    return indexCurrentDate
        ? allWeekdays.slice(indexCurrentDate).concat(allWeekdays.slice(0, indexCurrentDate))
        : allWeekdays;
};
export const PickupPoint = ({ pickupPoint, selected }) => {
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const { market } = useAemConfig();
    const [, dispatch] = useDeliveryState();
    const { address, city, distance, houseNumber, locationName, locationType, openingHours, organisationName, postcode, sanitizedLocationType, } = pickupPoint;
    const organisationNameWithType = `${organisationName}${PickupPointUtil.getTranslation(sanitizedLocationType, locationType, t)}`;
    const fullAdress = `${organisationNameWithType.trim()}, ${address}${houseNumber ? ` ${houseNumber}` : ''}, ${postcode} ${city}`;
    const isAlwaysOpen = PickupPointUtil.isAlwaysOpen(sanitizedLocationType || locationType);
    const getDistance = (market, distance, organisationName) => {
        if (market === 'uk') {
            return distance;
        }
        if (organisationName === Provider.DPD) {
            return round(distance, 0.1);
        }
        return round(distance / 1000, 0.1);
    };
    const onClick = () => {
        dispatch({ type: 'highlight_pickup_point', payload: pickupPoint });
    };
    return (_jsxs(SelectionCard, { state: selected ? 'active' : 'resting', onClick: onClick, testId: "pickuppoint_finder_item", children: [_jsx(SelectionCard.Cell, { section: "header", children: _jsx(TextAlpha, { variant: "subheadingS", children: locationName }) }), _jsx(SelectionCard.Cell, { section: "tag", children: _jsx(TextAlpha, { variant: "bodyS", strong: true, children: `${getDistance(market, distance, organisationName)} ${t(market === 'uk'
                        ? 'checkout.delivery.pickuppoint.flyout.distance.unit.miles'
                        : 'checkout.delivery.pickuppoint.flyout.distance.unit.km')}` }) }), _jsx(SelectionCard.Cell, { section: "content", children: _jsxs(StackInline, { gap: "2", itemsAlign: "start", wrap: "nowrap", children: [_jsx(Box, { paddingBlockStart: "1", children: _jsx(Icon, { source: MapPinIcon, size: "xs", color: "neutral" }) }), _jsx(TextAlpha, { children: fullAdress })] }) }), selected && (_jsx(SelectionCard.Cell, { section: "content", children: !!openingHours.length && (_jsx(_Fragment, { children: _jsxs(StackInline, { gap: "2", itemsAlign: "start", wrap: "nowrap", children: [_jsx(Box, { paddingBlockStart: "1", children: _jsx(Icon, { source: ClockIcon, size: "xs", color: "neutral" }) }), isAlwaysOpen && selected ? (_jsx(TextAlpha, { children: t('checkout.delivery.pickuppoint.open.24') })) : (selected && (_jsxs(StackBlock, { gap: "1", children: [_jsx(TextAlpha, { strong: true, children: t('checkout.delivery.pickuppoint.openinghours.week') }), _jsx(OpeningHours, { openingHours: sortOpeningHours(openingHours) })] })))] }) })) }))] }));
};
